import { useEffect, useMemo, useState } from 'react';

import { getLocale, getTargetShopUrl, pushAddToCartEvent, pushViewItemEvent } from '~helpers';
import {
  Product,
  ProductCategory,
  ProductCategoryValidity,
  Simulation,
  Timeslot,
  UpsellProposal,
} from '~types/appsync.types';
import { Participant } from '~types/participant';
import { getUrlAddToCart, parseCalendarDate } from '~utils';

import useSimulation from '../useSimulation/useSimulation';

export interface UpsellSimulationOverride {
  productSlug: string;
  productCategoryShortname: string;
  validityCategoryShortname: string;
  date?: string;
}

export interface UseSimulationManagerOptions {
  selectedProduct?: Product;
  date?: Date;
  participants: Participant[];
  productCategory?: ProductCategory;
  validityCategory?: ProductCategoryValidity;
  selectedTimeslot?: Timeslot;
  productLocked: boolean;
  upsellSimulationOverride?: UpsellSimulationOverride | null;
  /**
   * When provided, replaces the external redirect on add-to-cart click.
   * Used by PurchaseFlow to intercept the click and drive the internal cart flow
   * when `data-enable-cart` is active.
   */
  onAddToCartInternal?: () => void;
}

export interface UseSimulationManagerResult {
  simulation: Simulation | undefined;
  previousSimulation: Simulation | undefined;
  loadingSimulation: boolean;
  isSimulationOnError: boolean;
  simulationDate: Date | undefined;
  availableUpsellings: UpsellProposal[];
  handleAddToCartClick: () => void;
}

const useSimulationManager = ({
  selectedProduct,
  date,
  participants,
  productCategory,
  validityCategory,
  selectedTimeslot,
  productLocked,
  upsellSimulationOverride,
  onAddToCartInternal,
}: UseSimulationManagerOptions): UseSimulationManagerResult => {
  const [isSimulationOnError, setIsSimulationOnError] = useState(false);

  const simulationDate = useMemo<Date | undefined>(() => {
    if (!selectedProduct) return undefined;
    if (selectedProduct.isBooking && !selectedTimeslot) return undefined;
    return selectedProduct.calendarDisplay ? date : new Date();
  }, [selectedProduct, date, selectedTimeslot]);

  // Override date when upsell defines a future first_skidate (without changing user selection)
  const effectiveSimulationDate = useMemo<Date | undefined>(() => {
    if (upsellSimulationOverride?.date) {
      const overrideDate = parseCalendarDate(upsellSimulationOverride.date);
      return Number.isNaN(overrideDate.getTime()) ? simulationDate : overrideDate;
    }
    return simulationDate;
  }, [upsellSimulationOverride?.date, simulationDate]);

  const [simulation, previousSimulation, loadingSimulation, error] = useSimulation({
    catalogSelectable: selectedProduct?.catalog.selectable ?? true,
    contractorId: selectedProduct?.catalog.contractor,
    date: effectiveSimulationDate,
    forceSkip: productLocked,
    participants,
    productCategoryShortname:
      upsellSimulationOverride?.productCategoryShortname ?? productCategory?.shortname,
    productCategoryVisible: productCategory?.visible ?? true,
    productSlug: upsellSimulationOverride?.productSlug ?? selectedProduct?.slug,
    validityCategoryShortname:
      upsellSimulationOverride?.validityCategoryShortname ?? validityCategory?.shortname,
    validityCategoryVisible: validityCategory?.visible ?? true,
  });

  const locale = getLocale();
  const targetShopUrl = getTargetShopUrl();

  // Simulation error detection
  useEffect(() => {
    if (error) {
      setIsSimulationOnError(true);
      return;
    }

    if (simulation) {
      if (
        simulation?.orderItems.length === 0 ||
        simulation?.orderItems.length !== participants.length
      ) {
        setIsSimulationOnError(true);
        return;
      }
    }
    setIsSimulationOnError(false);
  }, [error, participants, simulation]);

  // Push analytics view_item event
  useEffect(() => {
    const isValidSimulation =
      simulation &&
      !loadingSimulation &&
      !error &&
      simulation.orderItems.length > 0 &&
      simulation.orderItems.length === participants.length;

    if (isValidSimulation && participants.length > 0) {
      pushViewItemEvent({
        date,
        participants,
        productCategory,
        simulation,
        validityCategory,
      });
    }
  }, [simulation, loadingSimulation, error, participants, date, productCategory, validityCategory]);

  const handleAddToCartClick = () => {
    if (simulation && participants.length > 0) {
      pushAddToCartEvent({
        date,
        participants,
        productCategory,
        simulation,
        validityCategory,
      });
    }

    if (onAddToCartInternal) {
      onAddToCartInternal();
      return;
    }

    globalThis.location.href = getUrlAddToCart(
      targetShopUrl,
      selectedProduct?.slug,
      simulationDate,
      participants,
      locale,
    );
  };

  const availableUpsellings = simulation?.availableUpsellings ?? [];

  return {
    availableUpsellings,
    handleAddToCartClick,
    isSimulationOnError,
    loadingSimulation,
    previousSimulation,
    simulation,
    simulationDate,
  };
};

export default useSimulationManager;
